import { useState } from "react"
import { DateRangePicker } from "../../shared/dateRangePicker"
import { SearchSelect } from "../../shared/searchSelect"
import { SearchFiltersModal } from "../../shared/searchFiltersModal"
import stringHelpers from "../../../utilities/stringHelpers"
import { reportDatePresetByLabel } from "../reportsEnums"
import { toDateString, dateRange } from "../../../utilities/dateUtilities"
import { TagSelector } from "../../shared/tagSelector"
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities"
import arrayUtilities from "../../../utilities/arrayUtilities"

const transferOfLiabilitiesGroupByOptions = { 0: "Site", 1: "Tag" }

export const TransferOfLiabilitiesReportFiltersModal = ({
    showModal,
    handleClose,
    handleConfirm,
    initialFromDate,
    initialToDate,
    initialDatePreset,
    initialSelectedUser,
    initialTransferOfLiabilitiesGroupBy,
    initialSelectedTags = [],
    initialAppliedFilters,
    users,
    tags = []
}) => {
    const [fromDate, setFromDate] = useState(initialFromDate)
    const [toDate, setToDate] = useState(initialToDate)
    const [datePreset, setDatePreset] = useState(initialDatePreset)
    const [selectedUser, setSelectedUser] = useState(initialSelectedUser)
    const [transferOfLiabilitiesGroupBy, setTransferOfLiabilitiesGroupBy] = useState(initialTransferOfLiabilitiesGroupBy)
    const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

    const handleTagsChange = tagSelections => {
        setSelectedTags(tagSelections)
        setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
    }

    const isDateRangeInvalid = fromDate === null || fromDate === "" || toDate === null || toDate === ""

    const clearLocalFilters = () => {
        const defaultRange = dateRange("lastMonth")
        setFromDate(defaultRange.firstDate)
        setToDate(defaultRange.lastDate)
        setDatePreset(reportDatePresetByLabel["Last month"])
        setSelectedUser("")
        setTransferOfLiabilitiesGroupBy(0)
        setSelectedTags([])
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`,
            "Group By": "Site"
        })
    }

    return (
        <SearchFiltersModal
            title="Filter Results"
            handleClose={ handleClose }
            handleConfirm={ () => handleConfirm({ fromDate, toDate, datePreset, selectedUser, transferOfLiabilitiesGroupBy, selectedTags, appliedFilters }) }
            showModal={ showModal }
            key="transfer-of-liabilities-report-filters-modal"
            handleReset={ clearLocalFilters }
            disableConfirm={ isDateRangeInvalid }
        >
            <div className="w-100">
                <div className="mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
                    <DateRangePicker
                        fromDate={ fromDate }
                        toDate={ toDate }
                        setDateRange={ (firstDate, lastDate, presetLabel) => {
                            const isFirstDateEmpty = firstDate === null || firstDate === ""
                            const isLastDateEmpty = lastDate === null || lastDate === ""
                            setFromDate(isFirstDateEmpty ? null : firstDate);
                            setToDate(isLastDateEmpty ? null : lastDate);
                            setDatePreset(stringHelpers.isNullOrEmpty(presetLabel) ? null : (reportDatePresetByLabel[presetLabel] ?? null))
                            if (!stringHelpers.isNullOrEmpty(presetLabel)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    updatedFilters["Date Range"] = presetLabel
                                    return updatedFilters
                                })
                            }
                            else if (!isFirstDateEmpty && !isLastDateEmpty) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    updatedFilters["Date Range"] = `${ toDateString(new Date(firstDate)) } - ${ toDateString(new Date(lastDate)) }`
                                    return updatedFilters
                                })
                            }
                        } }
                        hideLabel
                        fullWidth
                        dropdownPositionRight
                    />
                    { isDateRangeInvalid && <p className="text-danger fs--1 m-0 mt-1">This field cannot be cleared</p> }
                </div>

                { !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <label className="m-0 mr-2 mb-1 nowrap">Tags</label>
                        <button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
                    </div>
                    <TagSelector
                        onChange={ handleTagsChange }
                        selectedTags={ selectedTags }
                        tags={ tags }
                    />
                </div> }

                { users && <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">User</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={
                            [
                                { value: "", label: "All" },
                                ...users
                                    .map(item => ({ value: item.id, label: `${ item.firstName } ${ item.lastName }` }))
                            ]
                        }
                        onSelect={ value => {
                            setSelectedUser(value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["User"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } else {
                                const selectedUserItem = users.find(item => item.id === value)
                                setAppliedFilters(prevState => ({ ...prevState, "User": `${ selectedUserItem?.firstName } ${ selectedUserItem?.lastName }` }))
                            }
                        } }
                        selected={ selectedUser }
                    />
                </div> }

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Group By</label>
                    <select
                        id="group-by-selector"
                        className="border rounded px-2 py-1 w-100"
                        value={ String(transferOfLiabilitiesGroupBy) }
                        onChange={ e => {
                            const value = Number(e.target.value)
                            setTransferOfLiabilitiesGroupBy(value)
                            setAppliedFilters(prevState => ({
                                ...prevState,
                                "Group By": transferOfLiabilitiesGroupByOptions[value]
                            }))
                        } }
                    >
                        <option value="0">Site</option>
                        <option value="1">Tag</option>
                    </select>
                </div>
            </div>
        </SearchFiltersModal>
    )
}
